import { DialogDescription } from "@radix-ui/react-dialog";
import Bookkeeper from "components/company/Bookkeeper";
import IndustrySector from "components/company/IndustrySector";
import CompanyIssuerExternalLinks from "components/company/v2/CompanyIssuerExternalLinks";
import InBookmarksTag from "components/company/v2/InBookmarksTag";
import InPortfolioIndicator from "components/company/v2/InPortfolioTag";
import classNames from "components/ui/classNames";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "components/ui/Dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/Tooltip";
import formatDistanceStrict from "date-fns/formatDistanceStrict";
import getYear from "date-fns/getYear";
import lightFormat from "date-fns/lightFormat";
import enUS from "date-fns/locale/en-US";
import ptBR from "date-fns/locale/pt-BR";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import useSelfie from "lib/hooks/useSelfie";
import TickerSymbol from "lib/securities/TickerSymbol";
import formatCep from "lib/utils/formatCep";
import formatCnpj from "lib/utils/formatCnpj";
import parseDateYmd from "lib/utils/parseDateYmd";
import Link from "next/link";
import numeral from "numeral";
import { IAssetType, ISecurityCountry } from "types/graphqlTypes";
import type { IBrazilianRealEstateFundTopLevelFragment } from "lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import CompanyActionsDropdown from "components/company/v2/CompanyActionsDropdown";
import { FormattedMessage } from "react-intl";
import { format } from "util";

interface ICompanyIssuerHeaderCardProps {
  issuer:
    | IBrazilianCompanyIssuerTopLevelFragment
    | IBrazilianRealEstateFundTopLevelFragment
    | IAmericanCompanyIssuerTopLevelFragment;
  security:
    | IBrazilianCompanyIssuerTopLevelFragment["securities"][0]
    | IAmericanCompanyIssuerTopLevelFragment["securities"][0]
    | null;
  tickerSymbol: string;
}

export default function CompanyIssuerHeaderCard({ issuer, security, tickerSymbol }: ICompanyIssuerHeaderCardProps) {
  const selfie = useSelfie();

  const isBrazilianIssuer = issuer.__typename === "BrazilianCompanyIssuer";
  const isBrazilianRealEstateFundIssuer = issuer.__typename === "BrazilianRealEstateFund";
  const isAmericanIssuer = issuer.__typename === "AmericanCompanyIssuer";

  if ((isBrazilianIssuer || isAmericanIssuer) && !security) {
    return null;
  }

  const tickerSymbolValue = security ? security.tickerSymbol : tickerSymbol;
  const businessDescription = getBusinessDescription(issuer);
  return (
    <div className="space-y-4">
      <div className="space-y-2 font-aeonik">
        <div className="flex w-[calc(100%-50px)] items-center">
          <h1 className="m-0 block overflow-hidden text-ellipsis whitespace-nowrap text-2xl font-bold text-gray-800">
            {issuer.name} • {tickerSymbolValue}
          </h1>

          {selfie && (
            <div className="ml-2 inline-flex">
              <CompanyActionsDropdown
                issuerId={issuer._id}
                tickerSymbol={tickerSymbolValue}
                securityCountry={isAmericanIssuer ? ISecurityCountry.Us : ISecurityCountry.Br}
                viewerDidBookmark={issuer.viewerDidBookmark}
                isInPortfolio={issuer.isInPortfolio}
              />
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {!isBrazilianRealEstateFundIssuer && issuer.securities.length > 0 && (
            <div className="space-x-2">
              {issuer.securities.map(({ tickerSymbol }) => {
                return (
                  <Link
                    key={tickerSymbol}
                    href={format(isAmericanIssuer ? "/us/%s" : "/br/%s", tickerSymbol.toLowerCase())}
                    className={classNames(
                      "rounded-md px-2 py-1 text-xs font-bold tracking-wider dark:bg-gray-700 dark:text-gray-300",
                      "outline-none ring-violet-600 ring-offset-white focus-visible:ring-2 focus:focus-visible:ring-offset-2",
                      {
                        "bg-violet-600 text-white hover:bg-violet-600/80": tickerSymbolValue === tickerSymbol,
                        "bg-violet-50 text-violet-600 hover:bg-violet-100": tickerSymbolValue !== tickerSymbol,
                      },
                    )}
                  >
                    {tickerSymbol}
                  </Link>
                );
              })}

              {TickerSymbol.isCommonStockSymbol(tickerSymbolValue) && (
                <span
                  className={classNames(
                    "rounded-md px-2 py-1 text-xs font-bold tracking-wider dark:bg-gray-700 dark:text-gray-300",
                    "bg-emerald-50 text-emerald-600",
                  )}
                >
                  Ações Ordinárias
                </span>
              )}
              {TickerSymbol.isPreferredStockSymbol(tickerSymbolValue) && (
                <span
                  className={classNames(
                    "rounded-md px-2 py-1 text-xs font-bold tracking-wider dark:bg-gray-700 dark:text-gray-300",
                    "bg-orange-50 text-amber-700",
                  )}
                >
                  Ações Preferenciais
                </span>
              )}
              {TickerSymbol.isUnitStockSymbol(tickerSymbolValue) && (
                <span
                  className={classNames(
                    "rounded-md px-2 py-1 text-xs font-bold tracking-wider dark:bg-gray-700 dark:text-gray-300",
                    "bg-slate-50 text-slate-600",
                  )}
                >
                  Unit
                </span>
              )}
            </div>
          )}

          {isBrazilianIssuer && issuer.isBrazilianDepositaryReceipt && (
            <div>
              <span
                title="Brazilian Depositary Receipt"
                className="rounded-lg bg-blue-600 px-2 py-1 text-sm font-bold italic text-white"
              >
                BDR
              </span>
            </div>
          )}

          {isBrazilianRealEstateFundIssuer && issuer.isFiagro && (
            <div>
              <span className="rounded-lg bg-green-700 px-2 py-1 text-sm font-bold italic text-white">Fiagro</span>
            </div>
          )}

          {isAmericanIssuer && issuer.isAmericanDepositaryReceipt && (
            <div>
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger>
                    <span
                      title="American Depositary Receipt"
                      className="rounded-lg bg-blue-600 px-2 py-1 text-sm font-bold italic text-white"
                    >
                      ADR
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{getADRTooltipContent(issuer, tickerSymbol)}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </div>

        {issuer.standardLogo && (
          <img
            role="presentation"
            src={issuer.standardLogo.url}
            width={191}
            height={58}
            alt={issuer.name}
            draggable={false}
            className="absolute right-8 top-8 hidden h-14 select-none object-contain object-right xl:block"
          />
        )}

        {issuer.niceLogo && (
          <img
            role="presentation"
            src={issuer.niceLogo.url}
            width={48}
            height={48}
            alt={issuer.name}
            draggable={false}
            className="absolute right-8 top-8 block h-12 w-12 select-none object-contain object-right squircle xl:hidden"
          />
        )}
      </div>

      <p className="max-w-3xl text-sm font-normal text-gray-500 md:text-base">{businessDescription}</p>

      {!isBrazilianRealEstateFundIssuer && (
        <div>
          <IndustrySector assetType={getAssetType(issuer)} industrySector={issuer.sector} className="tracking-wide" />
        </div>
      )}

      <div
        className={classNames("flex flex-col items-center space-y-4 xl:flex-row xl:space-x-2 xl:space-y-0", {
          "items-start xl:flex-col xl:space-x-0 xl:space-y-2": isBrazilianRealEstateFundIssuer,
        })}
      >
        {!isBrazilianRealEstateFundIssuer && <MoreDetailsDialog issuer={issuer} />}
        {isBrazilianRealEstateFundIssuer && <CompensationPolicyDialog issuer={issuer} />}

        <CompanyIssuerExternalLinks issuer={issuer} />
      </div>

      <div className="space-x-2">
        {issuer.isInPortfolio && <InPortfolioIndicator />}
        {issuer.viewerDidBookmark && <InBookmarksTag />}
      </div>
    </div>
  );
}

interface IMoreDetailsDialogProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment | IAmericanCompanyIssuerTopLevelFragment;
}

function MoreDetailsDialog({ issuer }: IMoreDetailsDialogProps) {
  const isBrazilianIssuer = issuer.__typename === "BrazilianCompanyIssuer";
  const isAmericanIssuer = issuer.__typename === "AmericanCompanyIssuer";

  const cvmRegistrationDate =
    isBrazilianIssuer && issuer.cvmRegistrationDate ? parseDateYmd(issuer.cvmRegistrationDate) : null;

  return (
    <Dialog>
      <DialogTrigger
        className={classNames(
          "flex rounded-xl bg-gray-100 p-2 px-4 font-aeonik text-sm font-medium tracking-wide text-gray-600",
          "w-full justify-center xl:w-auto",
          "border border-transparent",
          "hover:border-gray-300",
        )}
      >
        <FormattedMessage
          defaultMessage="Ver mais detalhes"
          id="u6esZn"
          description="Botão de detalhes na capa da empresa"
        />
      </DialogTrigger>

      <DialogContent className="border border-slate-300 shadow-xl">
        <DialogHeader className="space-y-0">
          <DialogTitle className="text-2xl">{isAmericanIssuer ? "Company Details" : "Detalhes da Empresa"}</DialogTitle>
          <DialogDescription className="text-sm font-medium italic tracking-wide text-slate-500">
            {isAmericanIssuer
              ? "Additional company information, address, etc."
              : "Informações adicionais da empresa, endereço, auditoria, etc."}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto overflow-x-hidden">
          <dl className="group m-0 text-sm font-medium leading-5 text-slate-700 [&>div>&:nth-of-type(odd)]:bg-slate-100">
            <div className="grid grid-cols-2 px-3 py-2">
              <dt className="text-sm font-medium tracking-wide text-slate-600">
                {isBrazilianIssuer ? "Sede" : "Headquarters"}
              </dt>
              <dd>
                <Address issuer={issuer} />
              </dd>
            </div>

            {issuer.foundedYear && (
              <div className="grid grid-cols-2 px-3 py-2">
                <dt className="text-sm font-medium tracking-wide text-slate-600">
                  {isBrazilianIssuer ? "Ano de Fundação" : "Fundation"}
                </dt>
                <dd>{issuer.foundedYear}</dd>
              </div>
            )}

            {cvmRegistrationDate && (
              <div className="grid grid-cols-2 px-3 py-2">
                <dt className="text-sm font-medium tracking-wide text-slate-600">Listagem na CVM</dt>
                <dd>
                  {lightFormat(cvmRegistrationDate, "dd/MM/yyyy")}{" "}
                  <span className="text-xs italic text-slate-500">
                    ({distanceSinceToday(cvmRegistrationDate, ptBR)})
                  </span>
                </dd>
              </div>
            )}

            {isAmericanIssuer && issuer.ipoDate && (
              <div className="grid grid-cols-2 px-3 py-2">
                <dt className="text-sm font-medium tracking-wide text-slate-600">IPO</dt>
                <dd>
                  {lightFormat(parseDateYmd(issuer.ipoDate), "dd/MM/yyyy")}{" "}
                  <span className="text-xs italic text-slate-500">
                    ({distanceSinceToday(parseDateYmd(issuer.ipoDate), enUS)})
                  </span>
                </dd>
              </div>
            )}

            {issuer.employeeNumbers && (
              <div className="grid grid-cols-2 px-3 py-2">
                <dt className="text-sm font-medium tracking-wide text-slate-600">
                  {isBrazilianIssuer ? "N° de Empregados" : "Employees"}
                </dt>
                <dd>
                  {numeral(issuer.employeeNumbers.totalOfEmployees).format("0,0").replace(",", ".")}{" "}
                  <span className="text-xs italic text-slate-500">
                    ({getYear(parseDateYmd(issuer.employeeNumbers.asOfDate))})
                  </span>
                </dd>
              </div>
            )}

            {isBrazilianIssuer && issuer.website && (
              <div className="grid grid-cols-2 px-3 py-2">
                <dt className="text-sm font-medium tracking-wide text-slate-600">Website</dt>
                <dd>
                  <a
                    href={issuer.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 hover:underline"
                  >
                    {new URL(issuer.website).host}
                  </a>
                </dd>
              </div>
            )}

            {isAmericanIssuer && issuer.websiteHref && (
              <div className="grid grid-cols-2 px-3 py-2">
                <dt className="text-sm font-medium tracking-wide text-slate-600">Website</dt>
                <dd>
                  <a
                    href={issuer.websiteHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 hover:underline"
                  >
                    {new URL(issuer.websiteHref).host}
                  </a>
                </dd>
              </div>
            )}

            {isBrazilianIssuer && (
              <div className="grid grid-cols-2 px-3 py-2">
                <dt className="text-sm font-medium tracking-wide text-slate-600">IRPF</dt>
                <dd>{formatCnpj(issuer.cnpj)}</dd>
              </div>
            )}

            {isAmericanIssuer && issuer.irs && (
              <div className="grid grid-cols-2 px-3 py-2">
                <dt className="text-sm font-medium tracking-wide text-slate-600">IRS</dt>
                <dd>{formatCnpj(issuer.irs)}</dd>
              </div>
            )}

            {isBrazilianIssuer && issuer.bookkeeper && (
              <div className="grid grid-cols-2 px-3 py-2">
                <dt className="text-sm font-medium tracking-wide text-slate-600">Escriturador</dt>
                <dd>
                  <div className="relative">
                    <Bookkeeper bookkeeper={issuer.bookkeeper} side="top" />
                  </div>
                </dd>
              </div>
            )}

            {isBrazilianIssuer && issuer.auditor && (
              <div className="grid grid-cols-2 px-3 py-2">
                <dt className="text-sm font-medium tracking-wide text-slate-600">Auditoria</dt>
                <dd>
                  <div>{issuer.auditor.name}</div>
                </dd>
              </div>
            )}

            {isBrazilianIssuer && issuer.riskFactorsUrl && (
              <div className="grid grid-cols-2 px-3 py-2">
                <dt className="text-sm font-medium tracking-wide text-slate-600">Fatores de Risco</dt>
                <dd>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={issuer.riskFactorsUrl}
                    className="text-sky-600 hover:underline"
                  >
                    Visualizar
                  </a>
                </dd>
              </div>
            )}

            {isBrazilianIssuer && (
              <div className="grid grid-cols-2 px-3 py-2">
                <dt className="text-sm font-medium tracking-wide text-slate-600">Breve Histórico</dt>
                <dd>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={issuer.briefHistoryUrl}
                    className="text-sky-600 hover:underline"
                  >
                    Visualizar
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function distanceSinceToday(pastDate: Date, locale: Locale): string {
  return formatDistanceStrict(pastDate, new Date(), {
    locale,
  });
}

interface IAddressProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment | IAmericanCompanyIssuerTopLevelFragment;
}

function Address({ issuer }: IAddressProps) {
  const isBrazilianIssuer = issuer.__typename === "BrazilianCompanyIssuer";
  const isAmericanIssuer = issuer.__typename === "AmericanCompanyIssuer";

  if (isBrazilianIssuer && issuer.headquarters.country === "Brasil") {
    return (
      <div className="text-sm text-slate-600">
        <p>
          {issuer.headquarters.street}, {issuer.headquarters.neighborhood}
        </p>
        <p>
          {issuer.headquarters.city}, {issuer.headquarters.state}
        </p>
        <p>{formatCep(issuer.headquarters.postalCode)}</p>
      </div>
    );
  }

  if (isAmericanIssuer && issuer.address) {
    return (
      <div className="text-sm text-slate-600">
        <p>{issuer.address.addressLine1}</p>
        <p>
          {issuer.address.city}, {issuer.address.state}
        </p>
      </div>
    );
  }

  return null;
}

function getADRTooltipContent(issuer: IAmericanCompanyIssuerTopLevelFragment, tickerSymbol: string) {
  const security = issuer.securities.find((candidate) => {
    return candidate.tickerSymbol === tickerSymbol;
  });

  if (security && security.depositoryReceiptRatio) {
    return `Ratio: ${security.depositoryReceiptRatio}`;
  }
  return "American Depositary Receipt";
}

function getAssetType(
  issuer:
    | IBrazilianCompanyIssuerTopLevelFragment
    | IBrazilianRealEstateFundTopLevelFragment
    | IAmericanCompanyIssuerTopLevelFragment,
) {
  if (issuer.__typename === "AmericanCompanyIssuer") {
    return issuer.isREIT ? IAssetType.AmericanReit : IAssetType.AmericanCompany;
  }

  if (issuer.__typename === "BrazilianCompanyIssuer") {
    return IAssetType.BrazilianCompany;
  }

  return IAssetType.BrazilianRealEstateFund;
}

function getBusinessDescription(
  issuer:
    | IBrazilianCompanyIssuerTopLevelFragment
    | IBrazilianRealEstateFundTopLevelFragment
    | IAmericanCompanyIssuerTopLevelFragment,
) {
  if (issuer.__typename === "AmericanCompanyIssuer") {
    return issuer.shortBusinessDescription;
  }

  if (issuer.__typename === "BrazilianCompanyIssuer") {
    return issuer.businessDescription;
  }

  return issuer.shortDescription;
}

interface ICompensationPolicyDialogProps {
  issuer: IBrazilianRealEstateFundTopLevelFragment;
}

function CompensationPolicyDialog({ issuer }: ICompensationPolicyDialogProps) {
  return (
    <Dialog>
      <DialogTrigger
        className={classNames(
          "flex rounded-xl bg-gray-100 p-2 px-4 font-aeonik text-sm font-medium tracking-wide text-gray-600",
          "w-max justify-center border border-transparent hover:border-gray-300 xl:w-auto",
        )}
      >
        <p className="flex">Ver política de remuneração</p>
      </DialogTrigger>
      <DialogContent className="w-full max-w-3xl">
        <DialogHeader className="mb-4">
          <DialogTitle>Política de Remuneração da Administração</DialogTitle>
        </DialogHeader>

        {issuer.administrator.compensationPolicy}
      </DialogContent>
    </Dialog>
  );
}
